import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Box,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import AleaInput from '../../components/AleaInput/AleaInput.tsx';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';
import { IOrganisation } from '../../interfaces';
import OrganisationService from '../../services/OrganisationService.ts';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import { CountryCode } from '../../constants/Countries.ts';
import AleaCountry from '../../components/AleaCountry/AleaCountry.tsx';
import { useManifest } from '../../context/ManifestContext.tsx';
import {
  resolveCountryCode,
  resolveCountryNameForPayload,
  toLocalizedAleaCountryOptions,
  toManifestCountryCodeOptions,
} from '../../utils/manifestCountry.ts';

interface AleaEditOrganisationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organisation?: IOrganisation | null;
  onSaved?: (updated: IOrganisation) => void;
}

interface EditOrganisationValues {
  name: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: CountryCode | '';
  website: string;
}

const AleaEditOrganisationModal: React.FC<AleaEditOrganisationModalProps> = ({
  isOpen,
  onClose,
  organisation,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const { countries, isLoading: isManifestLoading } = useManifest();
  const manifestCountries = toManifestCountryCodeOptions(countries);

  const form = useForm<EditOrganisationValues>({
    defaultValues: {
      name: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: CountryCode.DE,
      website: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().required(t('requiredField', { field: t('nameOfOrganization') })),
        street: yup.string().required(t('requiredField', { field: t('street') })),
        houseNumber: yup.string().required(t('requiredField', { field: t('houseNumber') })),
        postalCode: yup.string().required(t('requiredField', { field: t('postalCode') })),
        city: yup.string().required(t('requiredField', { field: t('city') })),
        country: yup.string().required(t('requiredField', { field: t('country') })),
        website: yup.string().required(t('requiredField', { field: t('website') })),
      })
    ) as never,
    mode: 'all',
  });

  const sanitizeWebsiteForInput = (value?: string | null) => {
    if (!value) return '';
    try {
      let v = value.trim();
      v = v.replace(/^https?:\/\//i, '');
      v = v.replace(/^www\./i, '');
      return v;
    } catch {
      return value ?? '';
    }
  };

  useEffect(() => {
    if (!organisation) return;
    form.reset({
      name: organisation.name ?? '',
      street: organisation.address?.street_name ?? '',
      houseNumber: organisation.address?.house_number ?? '',
      postalCode: organisation.address?.postal_code ?? '',
      city: organisation.address?.city ?? '',
      country: resolveCountryCode(organisation.address?.country ?? '', manifestCountries),
      website: sanitizeWebsiteForInput(organisation.address?.website ?? ''),
    });
  }, [organisation, isOpen]);

  const handleSave = async () => {
    const valid = await form.trigger();
    if (!valid) return;
    if (!organisation?.id) {
      onClose();
      return;
    }

    const values = form.getValues();
    const websiteForSave = (() => {
      const current = values.website?.trim() || '';
      const original = organisation?.address?.website || '';
      if (original && sanitizeWebsiteForInput(original) === current) return original;
      return current;
    })();

    const addressPayload = {
      street_name: values.street,
      house_number: values.houseNumber,
      postal_code: values.postalCode,
      city: values.city,
      country: resolveCountryNameForPayload(values.country, manifestCountries, CountryCode.DE),
      phone_number: null,
      compound_phone_number: null,
      landline_phone_number: null,
      compound_landline_phone_number: null,
      email: '',
      website: websiteForSave,
    } as const;

    try {
      setIsSaving(true);
      const updated = await OrganisationService.updateOrganisationAddress(organisation.id, addressPayload);
      // Merge the updated address with the existing organisation data to maintain name
      const mergedOrganisation = {
        ...organisation,
        ...updated,
        address: updated.address || organisation.address,
      };
      onSaved?.(mergedOrganisation as IOrganisation);
      toast({
        title: t('success'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (e) {
      toast({
        title: t('error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Lower header z-index while modal is open (keeps modal above header)
  useEffect(() => {
    try {
      const ev = new CustomEvent('alea-global-modal-open', { detail: isOpen });
      window.dispatchEvent(ev);
    } catch {}
    return () => {
      try {
        const ev = new CustomEvent('alea-global-modal-open', { detail: false });
        window.dispatchEvent(ev);
      } catch {}
    };
  }, [isOpen]);

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay zIndex={1500} />
      <ModalContent
        data-testid="edit-organisation-modal"
        zIndex={1501}
        width="930px"
        maxW="930px"
        borderRadius="20px"
        bg="#F3F2F2"
      >
        <ModalHeader
          textAlign="left"
          color="#000"
          fontSize="25px"
          fontWeight={500}
          pt="50px"
          pb="30px"
          px="50px"
        >
          {t('masterData')}
        </ModalHeader>

        <ModalBody pt={0} px="50px" pb="30px">
          <Box>
            <Box mb="20px" width="50%">
              <AleaInput
                label={t('nameOfOrganization')}
                value={form.watch('name') || ''}
                onChange={(e) => form.setValue('name', e.target.value, { shouldValidate: true })}
                register={form.register('name')}
                error={form.formState.errors.name?.message}
              />
            </Box>

            <Flex gap="20px" mb="20px" alignItems="flex-start">
              <Box width="50%">
                <AleaInput
                  label={t('street')}
                  value={form.watch('street') || ''}
                  onChange={(e) => form.setValue('street', e.target.value, { shouldValidate: true })}
                  register={form.register('street')}
                  error={form.formState.errors.street?.message}
                />
              </Box>
              <Box width="180px">
                <AleaInput
                  label={t('houseNumber')}
                  value={form.watch('houseNumber') || ''}
                  onChange={(e) => form.setValue('houseNumber', e.target.value, { shouldValidate: true })}
                  register={form.register('houseNumber')}
                  error={form.formState.errors.houseNumber?.message}
                />
              </Box>
            </Flex>

            <Flex gap="20px" mb="20px" alignItems="flex-start">
              <Box width="50%">
                <Flex gap="20px">
                  <Box width="160px">
                    <AleaInput
                      label={t('postalCode')}
                      value={form.watch('postalCode') || ''}
                      onChange={(e) => form.setValue('postalCode', e.target.value, { shouldValidate: true })}
                      register={form.register('postalCode')}
                      error={form.formState.errors.postalCode?.message}
                    />
                  </Box>
                  <Box flex="1">
                    <AleaInput
                      label={t('city')}
                      value={form.watch('city') || ''}
                      onChange={(e) => form.setValue('city', e.target.value, { shouldValidate: true })}
                      register={form.register('city')}
                      error={form.formState.errors.city?.message}
                    />
                  </Box>
                </Flex>
              </Box>
              <Box width="50%">
                <AleaCountry
                  label={t('country')}
                  value={(form.watch('country') || '') as CountryCode | ''}
                  onChange={(code) => form.setValue('country', code as CountryCode, { shouldValidate: true })}
                  error={form.formState.errors.country?.message as string}
                  countryOptions={toLocalizedAleaCountryOptions(manifestCountries, t)}
                  isDisabled={isManifestLoading && manifestCountries.length === 0}
                />
              </Box>
            </Flex>

            <Box width="50%">
              <AleaInput
                label={t('website')}
                value={form.watch('website') || ''}
                onChange={(e) => form.setValue('website', e.target.value, { shouldValidate: true })}
                register={form.register('website')}
                error={form.formState.errors.website?.message}
                leftIcon={<Text color="#9AA1A4" pl="16px">www.</Text>}
                sx={{ paddingLeft: '63px' }}
              />
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter pt={0} pb="50px" px="50px">
          <Flex justifyContent="flex-end" alignItems="center" width="100%">
            <Button
              data-testid="edit-organisation-modal-cancel"
              variant="link"
              onClick={onClose}
              color="alea.500"
              textDecoration="underline"
              fontSize="18px"
              fontWeight={400}
            >
              {t('general.cancel')}
            </Button>
            <Button
              data-testid="edit-organisation-modal-save"
              colorScheme="alea"
              variant="solid"
              onClick={handleSave}
              h="50px"
              w="100px"
              borderRadius="20px"
              px="0"
              display="flex"
              alignItems="center"
              justifyContent="center"
              aria-label={t('save')}
              ml="25px"
            >
              <AleaCheckIcon color="white" size={29} />
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
    {isSaving && <AleaLoader />}
    </>
  );
};

export default AleaEditOrganisationModal;

